/**
 * Ortak sözlük ve tr-TR biçimleyicileri.
 *
 * ## Neden tek dosya
 *
 * Ürün sahibi giriş ekranına baktı ve *"onların ne claude yazılım benim yazılımım gözünü
 * seveyim"* dedi: CRM'i Türk bir ekip kullanıyor ama ekran baştan sona İngilizceydi. Ölçüldü —
 * `web/src` altında 233 İngilizce, 27 Türkçe dize, hepsi koda gömülü.
 *
 * Metni koda gömmek iki kusur üretiyor. Birincisi görünen: ekran yabancı dilde. İkincisi
 * görünmeyen ve daha pahalı: **bir metni değiştirmek kod değiştirmek** oluyor, ve hangi metnin
 * nerede olduğu hiçbir yerde görünmüyor. Bu yüzden önce altyapı kuruldu, sonra çeviri yapıldı.
 *
 * Desen v3'ten alındı (`src/site/messages.ts`): metin **veri**, koda serpiştirilmiş dize değil,
 * ve tip zorlaması eksik bir anahtarı derleme hatasına çeviriyor. v3'ten farkı tek dil: CRM iç
 * bir araç ve kullanıcısı tek bir ekip. İkinci dil gerektiğinde yapı hazır — bu dosyaların
 * yanına ikinci bir tablo gelir, çağıran kod değişmez.
 *
 * Yeni bir paket kurulmadı (`i18next` yok): depoda betiksiz ve bağımlılıksız çalışma alışkanlığı
 * var, ve bu ekranın ihtiyacı bir kütüphane değil bir tablo.
 *
 * ## Terim sözlüğü
 *
 * Aynı kavram her ekranda aynı kelimeyle anılıyor. Kararlar `docs/tr-sozluk.md` içinde ve
 * gerekçeleriyle yazılı; sonraki ajan oraya bakıp aynı kelimeyi kullanıyor.
 */

/** Ekranın dili. Tarih, sayı ve para biçimi hep bu yerelden okunuyor. */
export const YEREL = 'tr-TR';

/**
 * Kod değerlerinin ekran karşılıkları.
 *
 * **Kod değeri veritabanında olduğu gibi kalıyor.** `ADMIN`, `IN_FULFILMENT`, `PROSPECT`
 * çevrilmiyor; yalnız ekrana yazılırken karşılığı gösteriliyor. Kod değerini çevirmek defteri
 * bozardı: denetim kaydı, uç sözleşmesi ve göç dosyaları hep İngilizce sabitlere bakıyor.
 *
 * Karşılığı olmayan bir kod geldiğinde **kodun kendisi** yazılıyor. Uydurulmuş bir karşılık,
 * eksik bir karşılıktan kötüdür: eksik olan görünür, uydurulmuş olan görünmez.
 */
function esle(tablo: Readonly<Record<string, string>>) {
  return (kod: string | null | undefined): string => (kod ? (tablo[kod] ?? kod) : '');
}

/** Roller. `ADMIN` sistem yöneticisi, `MANAGER` müdür: ikisi ayrı yetki, ayrı kelime. */
export const ROL_ADI: Readonly<Record<string, string>> = {
  ADMIN: 'Sistem yöneticisi',
  CRM_SALES: 'Satış',
  OPERATIONS: 'Operasyon',
  FINANCE: 'Finans',
  MANAGER: 'Müdür',
  MARKETING: 'Pazarlama',
  // Beşinci bölümün rolü (30.08.2026). Ekranda kısaltma: gezinme çubuğu, sayfa başlığı ve rol
  // rozeti aynı kelimeyi taşısın diye.
  HR: 'İK',
  READ_ONLY: 'Salt okunur',
};
export const rolAdi = esle(ROL_ADI);

export const MUSTERI_DURUMU: Readonly<Record<string, string>> = {
  ACTIVE: 'Etkin',
  PROSPECT: 'Aday',
  INACTIVE: 'Pasif',
  BLOCKED: 'Engelli',
};
export const musteriDurumu = esle(MUSTERI_DURUMU);

export const MUSTERI_TURU: Readonly<Record<string, string>> = {
  INDIVIDUAL: 'Bireysel',
  COMPANY: 'Kurumsal',
};
export const musteriTuru = esle(MUSTERI_TURU);

/**
 * Durum adları, tek tabloda.
 *
 * Aynı sabit birden çok tabloda geçiyor (`CANCELLED` hem siparişte hem teklifte hem görevde) ve
 * hepsinde aynı şeyi anlatıyor: iptal. Ayrı tablolara bölmek, aynı kelimenin iki ekranda
 * ayrışmasına açık kapı bırakırdı.
 */
export const DURUM_ADI: Readonly<Record<string, string>> = {
  // Sipariş
  CREATED: 'Oluşturuldu',
  IN_FULFILMENT: 'Operasyonda',
  /**
   * Web siparişinin provizyon durumu (`WebOrderState.AUTHORIZED`).
   *
   * Karşılık "Provizyon alındı"; "Ödendi" değil ve olmamalı. Provizyon, paranın **bloke
   * edildiği ama tahsil edilmediği** an: sipariş satılmış sayılmıyor, iş kuyruğu açılmıyor.
   * Terimi ürün sahibi kullanıyor ve ekranın başka bir yerinde de aynı kelimeyle yazılı
   * (`metin/commerce.ts`, `provizyonNotu`), o yüzden burada da aynı kelime.
   */
  AUTHORIZED: 'Provizyon alındı',
  /**
   * Web siparişinin kalan iki durumu (`WebOrderState.PENDING_PAYMENT`, `WebOrderState.PAID`).
   * `AUTHORIZED` ve `CANCELLED` zaten bu tabloda; bu iki değer olmayınca kod adı çıplak
   * kalıyordu (ör. `/satis` panosundaki "Web sitesinden gelen siparişler" kuyruğu).
   */
  PENDING_PAYMENT: 'Ödeme bekliyor',
  PAID: 'Ödendi',
  // İade (04.10.2026, para yolu denetimi F1).
  PARTIALLY_REFUNDED: 'Kısmen iade edildi',
  REFUNDED: 'İade edildi',
  FULFILLED: 'Tamamlandı',
  COMPLETED: 'Kapatıldı',
  CANCELLED: 'İptal',
  // Teklif ve sürümü
  DRAFT: 'Taslak',
  PUBLISHED: 'Yayımlandı',
  ACCEPTED: 'Kabul edildi',
  REJECTED: 'Reddedildi',
  EXPIRED: 'Süresi doldu',
  SUPERSEDED: 'Yerine yenisi geçti',
  // Aday
  NEW: 'Yeni',
  QUALIFIED: 'Nitelendi',
  CONVERTED: 'Dönüştürüldü',
  DISQUALIFIED: 'Elendi',
  // Fırsat aşaması
  QUALIFYING: 'Niteleniyor',
  QUOTING: 'Teklif hazırlanıyor',
  WON: 'Kazanıldı',
  LOST: 'Kaybedildi',
  // Operasyon görevi
  PENDING: 'Bekliyor',
  IN_PROGRESS: 'Sürüyor',
  BLOCKED: 'Bloke',
  FAILED: 'Başarısız',
  // Tedarikçi rezervasyonu
  REQUESTED: 'Talep edildi',
  CONFIRMED: 'Teyitli',
  /**
   * Teklif talebinin (`QuoteRequestStatus`) üçüncü hâli: onaylanan talep bir fırsata bağlandı.
   * Öteki iki hâli (`DRAFT`, `CONFIRMED`) bu tabloda zaten vardı; `LINKED` yoktu ve talep
   * ekranında kod adı çıplak yazıyordu.
   */
  LINKED: 'Fırsata bağlandı',
  // Voucher
  ISSUED: 'Düzenlendi',
  VOID: 'Geçersiz',
  // Ödeme kanıtı
  RECORDED: 'Kaydedildi',
  VERIFIED: 'Doğrulandı',
  PARTIAL: 'Kısmi',
  FINAL: 'Nihai',
  // Maliyet
  EXPECTED: 'Beklenen',
  COMMITTED: 'Taahhüt edildi',
  REALIZED: 'Gerçekleşti',
  // Mutabakat
  READY: 'Hazır',
  RECONCILED: 'Mutabık',
  // Denetim sonucu
  SUCCESS: 'Başarılı',
  DENIED: 'Reddedildi',
  FAILURE: 'Hata',
  // Ödeme yöntemi
  BANK_TRANSFER: 'Banka havalesi',
  CARD: 'Kart',
  CASH: 'Nakit',
  // Entegrasyon durumu
  NOT_CONFIGURED: 'Tanımlı değil',
  PARTIALLY_CONFIGURED: 'Kısmen tanımlı',
  CONFIGURED: 'Tanımlı',
  // Maliyet kategorisi
  SUPPLIER: 'Tedarikçi',
  TAX: 'Vergi',
  FEE: 'Ücret',
  OTHER: 'Diğer',
  // Yaş aralığı (kuyruk tablolarındaki `ageBucket` sütunu, `ageBucketOf` uçta üretiyor)
  LT_24H: '24 saatten az',
  H24_48H: '24-48 saat',
  GT_48H: '48 saatten çok',
  // GeziPuan kredi durumu (credit.state) ve hareket türü (movement.kind, credit.earnKind)
  AVAILABLE: 'Harcanabilir',
  ORDER: 'Sipariş',
  INCENTIVE: 'Teşvik',
  EARN: 'Kazanç',
  SPEND: 'Harcama',
  RELEASE: 'Bırakıldı',
  EXPIRE: 'Süre doldu',
  CLAWBACK: 'Geri alındı',
  // Tedarikçi iptali (SupplierCancellation.state) ve vize talebi (VisaRequest.state, applicantType)
  OPEN: 'Bekliyor',
  SUBSTITUTED: 'Başka turla değiştirildi',
  RECEIVED: 'Alındı',
  SUBMITTED: 'Arkan\'a gönderildi',
  // Destek talebi durumu (SupplierQuestion.status, WebSupportCase.state) — iki ayrı tabloda aynı
  // üç değer (OPEN/ANSWERED/CLOSED) geçiyor, ikisi de aynı şeyi anlatıyor: bir soru bekliyor,
  // cevaplandı, ya da cevapsız kapandı.
  ANSWERED: 'Cevaplandı',
  CLOSED: 'Kapandı',
  ADULT: 'Yetişkin',
  CHILD: 'Çocuk',
  // Kanal kimliği durumu (CustomerChannelIdentity.state) — ACTIVE ve SUPERSEDED yukarıda zaten var
  DISPUTED: 'Anlaşmazlık',
};
export const durumAdi = esle(DURUM_ADI);

/** Sipariş kalemi türleri. Ürün adı (`ESIM`) çevrilmiyor: o ürünün adı. */
export const KALEM_TURU: Readonly<Record<string, string>> = {
  HOTEL: 'Otel',
  TRANSFER: 'Transfer',
  TOUR: 'Tur',
  VISA: 'Vize',
  HELICOPTER: 'Helikopter',
  YACHT: 'Yat',
  TICKET: 'Bilet',
  ESIM: 'eSIM',
  OTHER: 'Diğer',
};
export const kalemTuru = esle(KALEM_TURU);

/** Kanal kimliği türleri (`CustomerChannelKind`) — müşteriye hangi kanaldan ulaşılabildiği. */
export const KANAL_TURU: Readonly<Record<string, string>> = {
  INSTAGRAM: 'Instagram',
  FACEBOOK: 'Facebook',
  WHATSAPP: 'WhatsApp',
  EMAIL: 'E-posta',
  PHONE: 'Telefon',
};
export const kanalTuru = esle(KANAL_TURU);

/* ------------------------------------------------------------------ biçimleyiciler */

/**
 * Para.
 *
 * **Hepsi TL değil ve olduğunu varsaymak bugünün verisinde %100 yanlış olurdu.** Ölçüldü
 * (21.08.2026): `Currency` tablosunda dört para birimi var (EUR, GBP, TRY, USD, hepsi iki
 * ondalıklı) ve veritabanındaki 78 sipariş kaleminin **tamamı EUR**. Şemada her tutarın yanında
 * kendi `currencyCode` alanı duruyor (`prisma/schema.prisma`, BR-A2-004: işaretli 32 bit tam
 * sayı, açık para biriminin **alt biriminde**).
 *
 * O yüzden bu fonksiyon para birimini **istiyor**, varsaymıyor: çağıran, tutarı hangi alandan
 * okuduysa para birimini de oradan veriyor. Alt birim ikiye bölünüyor çünkü dört para biriminin
 * dördü de iki ondalıklı; bir gün üç ondalıklı bir para birimi eklenirse burası `Currency`
 * tablosundaki `minorUnits` alanını okumak zorunda kalacak ve o değişiklik burada tek yerde.
 */
export function para(altBirim: number | null | undefined, paraBirimi: string | null | undefined): string {
  if (altBirim === null || altBirim === undefined) return TANIMSIZ;
  const kod = paraBirimi ?? '';
  if (kod === '') return sayi(altBirim / 100);
  return new Intl.NumberFormat(YEREL, { style: 'currency', currency: kod }).format(altBirim / 100);
}

/**
 * Kuponun indirimi: sabit tutar `10,00 €`, yüzde `%10` (üst sınır varsa `%10, en çok 50,00 €`).
 * Yüzde baz puan olarak gelir (1000 = %10); 12,5 gibi kesirli yüzde de doğru yazılır.
 */
export function indirimYaz(indirim: {
  kind?: string;
  amountMinor?: number | null;
  currency?: string | null;
  percentBasisPoints?: number | null;
  maxDiscountMinor?: number | null;
}): string {
  if (indirim.kind !== 'PERCENT') return para(indirim.amountMinor, indirim.currency);
  if (indirim.percentBasisPoints === null || indirim.percentBasisPoints === undefined) return TANIMSIZ;
  const yuzde = `%${sayi(indirim.percentBasisPoints / 100)}`;
  return indirim.maxDiscountMinor === null || indirim.maxDiscountMinor === undefined
    ? yuzde
    : `${yuzde}, en çok ${para(indirim.maxDiscountMinor, indirim.currency)}`;
}

/** Sayı: binlik ayırıcı nokta, ondalık ayırıcı virgül. */
export function sayi(deger: number | null | undefined, basamak = 2): string {
  if (deger === null || deger === undefined || Number.isNaN(deger)) return TANIMSIZ;
  return new Intl.NumberFormat(YEREL, { minimumFractionDigits: 0, maximumFractionDigits: basamak }).format(deger);
}

/** Tam sayı: kayıt sayısı, sürüm numarası, yüzde. Ondalık göstermiyor. */
export function tamSayi(deger: number | null | undefined): string {
  return sayi(deger, 0);
}

/** Gün: 21.08.2026. Saat gerekmiyorsa saat yazılmıyor. */
export function tarih(deger: string | Date | null | undefined): string {
  const an = ana(deger);
  return an === null ? TANIMSIZ : new Intl.DateTimeFormat(YEREL, { dateStyle: 'short' }).format(an);
}

/** Gün ve saat: 21.08.2026 22:48. Bir kaydın ne zaman değiştiği bu biçimde okunuyor. */
export function tarihSaat(deger: string | Date | null | undefined): string {
  const an = ana(deger);
  return an === null ? TANIMSIZ : new Intl.DateTimeFormat(YEREL, { dateStyle: 'short', timeStyle: 'short' }).format(an);
}

function ana(deger: string | Date | null | undefined): Date | null {
  if (deger === null || deger === undefined || deger === '') return null;
  const an = deger instanceof Date ? deger : new Date(deger);
  return Number.isNaN(an.getTime()) ? null : an;
}

/**
 * Değeri olmayan alanın yazısı.
 *
 * Tek bir işaret, çünkü bir tablonun içinde "yok", "tanımsız", "belirtilmemiş" yan yana
 * durduğunda okuyan kişi aralarında bir fark arıyor ve fark yok.
 */
export const TANIMSIZ = '—';

/** Sözlükteki ortak kelimeler: birden çok ekranda geçen ve aynı kalması gereken metinler. */
export const ORTAK = {
  yukleniyor: 'Yükleniyor…',
  tarihSecin: 'Tarih seçin',
  tarihAraligiSecin: 'Giriş ve çıkış tarihi seçin',
  oncekiAy: 'Önceki ay',
  sonrakiAy: 'Sonraki ay',
  temizle: 'Temizle',
  tamam: 'Tamam',
  saat: 'Saat',
  cikisSecin: 'Çıkış tarihini seçin',
  geceSayisi: (gece: number) => `${gece} gece`,
  yenileniyor: 'Yenileniyor…',
  yeniden: 'Yeniden dene',
  kaydet: 'Kaydet',
  vazgec: 'Vazgeç',
  ara: 'Ara',
  ekle: 'Ekle',
  sil: 'Sil',
  ac: 'Aç',
  kapat: 'Kapat',
  onceki: 'Önceki',
  sonraki: 'Sonraki',
  sayfa: 'Sayfa',
  kayit: 'kayıt',
  toplam: 'Toplam',
  durum: 'Durum',
  tur: 'Tür',
  ad: 'Ad',
  eposta: 'E-posta',
  telefon: 'Telefon',
  musteri: 'Müşteri',
  siparis: 'Sipariş',
  teklif: 'Teklif',
  tedarikci: 'Tedarikçi',
  gorev: 'Görev',
  tutar: 'Tutar',
  paraBirimi: 'Para birimi',
  tarih: 'Tarih',
  belirtilmemis: 'Belirtilmemiş',
  atanmamis: 'Atanmamış',
  yok: 'Yok',
  kalem: 'kalem',
  acikBakiye: 'Açık bakiye',
  hataOlustu: 'Beklenmeyen bir hata oluştu.',
} as const;
